// src/components/layout/DealershipAccent.jsx
// STATUS: active | UPDATED: 2026-09-01
// LOG:
//   2026-09-01  New. A store's own colour on their own floor's screens.
//
// The accent has been captured per dealership since migration 0010 and applied
// nowhere a member could see it. This applies it.
//
// WHY CSS VARIABLES AND NOT A REBUILT THEME
//
// The obvious approach is extendTheme with the dealership's colour. It is also
// the dangerous one: AuthProvider sits inside ChakraProvider, so reading a
// profile before building the theme means restructuring the provider tree, and
// the theme is the documented trap in this repo.
//
// Chakra already emits every semantic token as a CSS custom property. Setting
// --chakra-colors-accent-500 on the document overrides it everywhere the token
// is used, with no theme rebuild, no remount and no provider surgery. If this
// component is removed the house oxblood returns on its own.
//
// THE HUE IS THEIRS, THE LIGHTNESS IS OURS
//
// See brandAccent.js. accent.500 paints small letterspaced eyebrow text and a
// button background with white on it, so a raw brand colour is frequently
// illegible. Their colour is moved just far enough to clear 4.5:1 against the
// ground it will actually sit on, in whichever mode the person is using.
//
// Nobody looks at an eyebrow and thinks "that is four percent darker than our
// logo". They think "that is our red".
//
// LEVEL 00 KEEPS OXBLOOD
//
// Jazz and Tyler have no dealership, and the operator surfaces should stay in
// the house colours. A super admin whose screens change colour depending on
// which store they last looked at would be worse, not better.

import { useEffect } from 'react'
import { useColorMode } from '@chakra-ui/react'
import { useAuth } from '../../hooks/useAuth'
import { brandAccent } from '../../lib/brandAccent'

const VARS = [
  '--chakra-colors-accent-500',
  '--chakra-colors-accent-600',
  '--chakra-colors-accent-soft',
]

export default function DealershipAccent() {
  const { dealership } = useAuth()
  const { colorMode } = useColorMode()
  const hex = dealership?.accent_hex || null

  useEffect(() => {
    const root = document.documentElement
    const clear = () => VARS.forEach((v) => root.style.removeProperty(v))

    if (!hex) {
      clear()
      return clear
    }

    const a = brandAccent(hex)
    if (!a) {
      // Their colour cannot be made legible in one of the two modes. The house
      // oxblood stays, because a brand is not worth an unreadable screen.
      clear()
      return clear
    }

    const dark = colorMode === 'dark'
    root.style.setProperty('--chakra-colors-accent-500', dark ? a.dark : a.light)
    root.style.setProperty('--chakra-colors-accent-600', dark ? a.dark600 : a.light600)
    root.style.setProperty('--chakra-colors-accent-soft', dark ? a.softDark : a.softLight)

    return clear
  }, [hex, colorMode])

  return null
}
